/**
 * Patient-friendly display names for the LOINC codes in the curated
 * `lizaPrimaryCareLabs` ValueSet.
 *
 * These are hand-curated copy, not raw LOINC data. LOINC publishes a
 * "Consumer Name" for each code (e.g. `4548-4` → "HbA1c/Total Hgb, Blood"),
 * but those are Alpha-status, carry a specimen suffix, and aren't as clean as
 * patient-facing labels should be — so we maintain our own short labels here.
 * The LOINC standard name is noted alongside each entry for reference.
 *
 * Used at display time only (the lab list, the lab detail heading, and the
 * pickers via `useCuratedLabItems`). The stored `Observation.code` is never
 * rewritten — only the rendered label.
 *
 * When adding a code to `lizaPrimaryCareLabs`, add its label here too; a test
 * in `@liza/medplum-config` asserts the two stay in sync.
 */
// Entries are sorted numerically by LOINC code. Order is for readability only — lookups are by key.
export const LOINC_LAB_DISPLAY_NAMES: Record<string, string> = {
  '718-7': 'Hemoglobin', // Hemoglobin [Mass/volume] in Blood
  '751-8': 'Neutrophils (Absolute Count)', // Neutrophils [#/volume] in Blood by Automated count
  '777-3': 'Platelets', // Platelets [#/volume] in Blood by Automated count
  '787-2': 'MCV', // MCV [Entitic volume] by Automated count
  '1558-6': 'Glucose, fasting', // Fasting glucose [Mass/volume] in Serum or Plasma
  '1742-6': 'ALT', // Alanine aminotransferase [Enzymatic activity/volume] in Serum or Plasma
  '1751-7': 'Albumin', // Albumin [Mass/volume] in Serum or Plasma
  '1884-6': 'ApoB', // Apolipoprotein B [Mass/volume] in Serum or Plasma
  '1920-8': 'AST', // Aspartate aminotransferase [Enzymatic activity/volume] in Serum or Plasma
  '1963-8': 'Bicarbonate', // Carbon dioxide, total [Moles/volume] in Serum or Plasma
  '1968-7': 'Direct Bilirubin', // Bilirubin.direct [Mass/volume] in Serum or Plasma
  '1975-2': 'Total Bilirubin', // Bilirubin.total [Mass/volume] in Serum or Plasma
  '2085-9': 'HDL Cholesterol', // Cholesterol in HDL [Mass/volume] in Serum or Plasma
  '2093-3': 'Total Cholesterol', // Cholesterol [Mass/volume] in Serum or Plasma
  '2132-9': 'Vitamin B12', // Cobalamin (vitamin B12) [Mass/volume] in Serum or Plasma
  '2157-6': 'CK', // Creatine kinase [Enzymatic activity/volume] in Serum or Plasma
  '2160-0': 'Creatinine', // Creatinine [Mass/volume] in Serum or Plasma
  '2191-5': 'DHEA-S', // Dehydroepiandrosterone sulfate (DHEA-S) [Mass/volume] in Serum or Plasma
  '2243-4': 'Estradiol', // Estradiol [Mass/volume] in Serum or Plasma
  '2276-4': 'Ferritin', // Ferritin [Mass/volume] in Serum or Plasma
  '2339-0': 'Blood glucose', // Glucose [Mass/volume] in Blood — fingerstick / home meter reading
  '2345-7': 'Glucose, random', // Glucose [Mass/volume] in Serum or Plasma
  '2498-4': 'Iron', // Iron [Mass/volume] in Serum or Plasma
  '2500-7': 'TIBC', // Iron binding capacity [Mass/volume] in Serum or Plasma
  '2571-8': 'Triglycerides', // Triglyceride [Mass/volume] in Serum or Plasma
  '2731-8': 'PTH', // Parathyrin [Units/volume] in Serum or Plasma
  '2777-1': 'Phosphate', // Phosphate [Mass/volume] in Serum or Plasma
  '2823-3': 'Potassium', // Potassium [Moles/volume] in Serum or Plasma
  '2839-9': 'Progesterone', // Progesterone [Mass/volume] in Serum or Plasma
  '2842-3': 'Prolactin', // Prolactin [Mass/volume] in Serum or Plasma
  '2857-1': 'PSA', // Prostate specific Ag [Mass/volume] in Serum or Plasma
  '2942-1': 'Sex Hormone-Binding Globulin', // Sex hormone binding globulin [Mass/volume] in Serum or Plasma
  '2951-2': 'Sodium', // Sodium [Moles/volume] in Serum or Plasma
  '2986-8': 'Testosterone', // Testosterone [Mass/volume] in Serum or Plasma
  '2991-8': 'Free Testosterone', // Testosterone Free [Mass/volume] in Serum or Plasma
  '3016-3': 'TSH', // Thyrotropin [Units/volume] in Serum or Plasma
  '3026-2': 'Free T4', // Thyroxine (T4) free [Mass/volume] in Serum or Plasma
  '3053-6': 'Total T3', // Triiodothyronine (T3) [Mass/volume] in Serum or Plasma
  '3084-1': 'Uric acid', // Urate [Mass/volume] in Serum or Plasma
  '4537-7': 'ESR', // Erythrocyte sedimentation rate by Westergren method
  '4544-3': 'Hematocrit', // Hematocrit [Volume Fraction] of Blood
  '4548-4': 'HbA1c', // Hemoglobin A1c/Hemoglobin.total in Blood
  '5671-3': 'Lead', // Lead [Mass/volume] in Blood
  '5902-2': 'PT', // Prothrombin time (PT)
  '6299-2': 'BUN', // Urea nitrogen [Mass/volume] in Blood
  '6301-6': 'INR', // INR in Platelet poor plasma by Coagulation assay
  '6690-2': 'WBC', // Leukocytes [#/volume] in Blood by Automated count
  '6768-6': 'Alkaline Phosphatase', // Alkaline phosphatase [Enzymatic activity/volume] in Serum or Plasma
  '8099-4': 'TPO Antibodies', // Thyroperoxidase Ab [Units/volume] in Serum or Plasma
  '9318-7': 'Urine Albumin-Creatinine Ratio', // Albumin/Creatinine [Mass Ratio] in Urine
  '10501-5': 'LH', // Luteinizing hormone [Units/volume] in Serum or Plasma
  '10835-7': 'Lp(a) (mg/dL)', // Lipoprotein a [Mass/volume] in Serum or Plasma
  '11572-5': 'Rheumatoid Factor', // Rheumatoid factor [Units/volume] in Serum or Plasma
  '13457-7': 'LDL Cholesterol', // Cholesterol in LDL [Mass/volume] in Serum or Plasma by calculation
  '14635-7': 'Vitamin D', // Vitamin D 25-hydroxy [Mass/volume] in Serum or Plasma
  '14979-9': 'aPTT', // aPTT in Platelet poor plasma by Coagulation assay
  '15067-2': 'FSH', // Follitropin [Units/volume] in Serum or Plasma
  '17861-6': 'Calcium', // Calcium [Mass/volume] in Serum or Plasma
  '19123-9': 'Magnesium', // Magnesium [Mass/volume] in Serum or Plasma
  '20150-9': 'FEV1', // FEV1 — forced expiratory volume in the first second
  '20447-9': 'HIV-1 RNA Viral Load', // HIV 1 RNA [#/volume] (viral load) in Serum or Plasma by NAA with probe detection
  '21198-7': 'Beta-hCG, Quant', // Choriogonadotropin.beta subunit [Units/volume] in Serum or Plasma
  '24467-3': 'CD4 Count', // CD3+CD4+ (T4 helper) cells [#/volume] in Blood
  '30522-7': 'hs-CRP', // C reactive protein [Mass/volume] in Serum or Plasma by High sensitivity method
  '32218-0': 'CCP Antibody', // Cyclic citrullinated peptide Ab [Units/volume] in Serum by Immunoassay
  '33452-4': 'Peak Expiratory Flow', // Maximum expiratory gas flow Respiratory system airway
  '33762-6': 'NT-proBNP', // Natriuretic peptide.B prohormone N-Terminal [Mass/volume] in Serum or Plasma
  '33914-3': 'eGFR', // Glomerular filtration rate/1.73 sq M.predicted
  // Lp(a) is reported in two units with a separate LOINC each, and labs use both. The unit is part
  // of the display because the values are not interconvertible, so a bare "Lp(a)" would render two
  // indistinguishable picker rows for two genuinely different measurements.
  '43583-0': 'Lp(a) (nmol/L)', // Lipoprotein a [Moles/volume] in Serum or Plasma
};

/** Patient-friendly label for a LOINC code, or `undefined` when the code isn't curated. */
export const getLoincLabDisplayName = (loincCode: string): string | undefined => LOINC_LAB_DISPLAY_NAMES[loincCode];

/**
 * Validated Spanish translations for LOINC-coded results (labs + vitals).
 * Clinical abbreviations and instrument names that are the same in Spanish (ALT, HbA1c, TSH, etc.)
 * are intentionally absent — they fall back to the English curated label.
 */
// Entries mirror LOINC_LAB_DISPLAY_NAMES order. Vital sign codes follow.
export const LOINC_DISPLAY_NAMES_ES: Record<string, string> = {
  // Labs
  '718-7': 'Hemoglobina',
  '751-8': 'Neutrófilos (Conteo Absoluto)',
  '777-3': 'Plaquetas',
  '1558-6': 'Glucosa en ayunas',
  '1751-7': 'Albúmina',
  '1963-8': 'Bicarbonato',
  '1968-7': 'Bilirrubina directa',
  '1975-2': 'Bilirrubina total',
  '2085-9': 'Colesterol HDL',
  '2093-3': 'Colesterol total',
  '2132-9': 'Vitamina B12',
  '2160-0': 'Creatinina',
  '2276-4': 'Ferritina',
  '2339-0': 'Glucosa en sangre',
  '2345-7': 'Glucosa casual',
  '2498-4': 'Hierro',
  '2571-8': 'Triglicéridos',
  '2777-1': 'Fosfato',
  '2823-3': 'Potasio',
  '2839-9': 'Progesterona',
  '2842-3': 'Prolactina',
  '2942-1': 'Globulina Fijadora de Hormonas Sexuales',
  '2951-2': 'Sodio',
  '2986-8': 'Testosterona',
  '2991-8': 'Testosterona Libre',
  '3084-1': 'Ácido úrico',
  '4544-3': 'Hematocrito',
  '5671-3': 'Plomo',
  '8099-4': 'Anticuerpos TPO',
  '9318-7': 'Albúmina-Creatinina en Orina',
  '11572-5': 'Factor Reumatoide',
  '13457-7': 'Colesterol LDL',
  '14635-7': 'Vitamina D',
  '17861-6': 'Calcio',
  '19123-9': 'Magnesio',
  '20447-9': 'Carga Viral de VIH-1 (ARN)',
  '21198-7': 'Beta-hCG Cuantitativa',
  '24467-3': 'Conteo de Células T CD4',
  '32218-0': 'Anticuerpo CCP',
  // Surveys with LOINC total-score codes
  '72514-3': 'Escala del Dolor (NRS)',
  // Vitals
  '8310-5': 'Temperatura',
  '8302-2': 'Estatura',
  '8867-4': 'Frecuencia cardíaca',
  '9279-1': 'Frecuencia respiratoria',
  '29463-7': 'Peso',
  '39156-5': 'Índice de masa corporal',
  '59408-5': 'Saturación de oxígeno',
  '85354-9': 'Presión arterial',
};

/**
 * Validated Spanish translations for result codes on the `survey-scores` system.
 * Only includes surveys with patient-facing translated display names.
 */
export const SURVEY_SCORES_DISPLAY_NAMES_ES: Record<string, string> = {
  'mood-scale': 'Estado de ánimo',
};
